import { useCallback, useEffect } from 'react';
import { surfaceConfig, type AppSurface } from '../appSurface';
import {
  claimTaskAlerts as claimTaskAlertsApi,
  getVapidPublicKey,
  subscribePush,
  unsubscribePush,
} from '../api/push';
import { useCurrentUser } from './useCurrentUser';
import { isStandaloneDisplayMode } from '../utils/displayMode';
import { listKnownPushSubscriptions } from '../utils/pushRegistrations';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

/** PP15: does the browser subscription's own applicationServerKey still
 * match what the server is handing out today? Browsers that don't expose
 * `subscription.options` at all (older engines) can't be checked — treat
 * that as "assume compatible" rather than forcing a churn-y resubscribe on
 * every call for a platform this can't verify on. */
function vapidKeyMatches(existing: ArrayBuffer | null | undefined, desired: Uint8Array): boolean {
  if (!existing) return true;
  const existingBytes = new Uint8Array(existing);
  if (existingBytes.length !== desired.length) return false;
  for (let i = 0; i < desired.length; i++) {
    if (existingBytes[i] !== desired[i]) return false;
  }
  return true;
}

export type SubscribeOutcome = 'subscribed' | 'permission_denied' | 'unsupported' | 'not_configured';
export type ClaimOutcome = 'claimed' | 'permission_denied' | 'unsupported' | 'not_configured' | 'device_registration_required';
export interface DevicePushEndpointProof {
  endpoints: string[];
  resolved: boolean;
}

export const ALERT_TRANSFER_INCOMPLETE_KEY = 'alert-transfer-incomplete';
export const ALERT_TRANSFER_STATE_EVENT = 'valentine:alert-transfer-state';

export function isAlertTransferIncomplete(surface: AppSurface): boolean {
  try { return localStorage.getItem(ALERT_TRANSFER_INCOMPLETE_KEY) === surface; } catch { return false; }
}

function notifyTransferStateChanged(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ALERT_TRANSFER_STATE_EVENT));
}

function markTransferIncomplete(surface: AppSurface): void {
  try {
    localStorage.setItem(ALERT_TRANSFER_INCOMPLETE_KEY, surface);
    notifyTransferStateChanged();
  } catch { /* storage unavailable */ }
}

function transferIncompleteFor(surface: AppSurface): boolean {
  return isAlertTransferIncomplete(surface);
}

function clearTransferIncomplete(surface: AppSurface): void {
  try {
    if (localStorage.getItem(ALERT_TRANSFER_INCOMPLETE_KEY) === surface) {
      localStorage.removeItem(ALERT_TRANSFER_INCOMPLETE_KEY);
      notifyTransferStateChanged();
    }
  } catch { /* storage unavailable */ }
}

type PreparedSubscription =
  | { subscription: PushSubscription }
  | { outcome: Exclude<SubscribeOutcome, 'subscribed'> };

async function waitForActiveWorker(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) return;
  const worker = registration.installing ?? registration.waiting;
  if (!worker) throw new Error('Requested service worker has no active worker');
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      worker.removeEventListener('statechange', onStateChange);
      reject(new Error('Requested service worker did not activate'));
    }, 10_000);
    const onStateChange = () => {
      if (worker.state !== 'activated' && !registration.active) return;
      window.clearTimeout(timeout);
      worker.removeEventListener('statechange', onStateChange);
      resolve();
    };
    worker.addEventListener('statechange', onStateChange);
    onStateChange();
  });
}

async function registrationForSurface(surface: AppSurface): Promise<ServiceWorkerRegistration> {
  const config = surfaceConfig(surface);
  const expectedScope = new URL(config.workerScope, window.location.origin).href;
  let registration = await navigator.serviceWorker.getRegistration(config.workerScope);
  if (!registration || registration.scope !== expectedScope) {
    if (typeof navigator.serviceWorker.register !== 'function') {
      throw new Error(`Missing exact service worker registration for ${surface}`);
    }
    registration = await navigator.serviceWorker.register(config.workerUrl, {
      scope: config.workerScope,
      updateViaCache: 'none',
    });
  }
  if (registration.scope !== expectedScope) {
    throw new Error(`Wrong service worker scope for ${surface}`);
  }
  await waitForActiveWorker(registration);
  return registration;
}

async function prepareSubscription(
  surface: AppSurface,
  allowPermissionRequest: boolean,
): Promise<PreparedSubscription> {
  if (
    typeof window === 'undefined'
    || !('serviceWorker' in navigator)
    || typeof PushManager === 'undefined'
    || typeof Notification === 'undefined'
  ) {
    return { outcome: 'unsupported' };
  }

  let permission = Notification.permission;
  if (permission !== 'granted' && allowPermissionRequest) {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return { outcome: 'permission_denied' };

  const vapid = await getVapidPublicKey().catch(() => null);
  if (!vapid || !vapid.configured || !vapid.public_key) return { outcome: 'not_configured' };

  const desiredKey = urlBase64ToUint8Array(vapid.public_key);
  const registration = await registrationForSurface(surface);
  let subscription = await registration.pushManager.getSubscription();

  if (subscription && !vapidKeyMatches(subscription.options?.applicationServerKey, desiredKey)) {
    await subscription.unsubscribe().catch(() => false);
    subscription = null;
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: desiredKey as BufferSource,
    });
  }
  return { subscription };
}

/** P3-A12's browser-side half of the subscribe flow — the coach's "Allow"
 * step (via App.tsx's `onPermissionGranted`) and MySettings' push-alert
 * toggle both call `subscribe()`. The SERVER remains the enforcement point
 * for opt-outs (each notification type is checked at send time) — this hook additionally keeps
 * the BROWSER subscription itself in sync so there's an actual device to
 * send to when the toggle is on, and no dangling subscription lingering
 * after it's turned off.
 *
 * Every step is feature-detected — a platform missing `serviceWorker`,
 * `PushManager`, or `Notification` gets a clean 'unsupported' with no
 * console noise. A missing/unprovisioned VAPID key (empty string, a 404,
 * or any other network hiccup fetching it) resolves to 'not_configured'
 * just as quietly — this covers prod environments where the VAPID keypair
 * hasn't been generated yet (see push_service.py's module docstring). */
export function usePushSubscription(surface: AppSurface = 'valentine') {
  const subscribe = useCallback(async (): Promise<SubscribeOutcome> => {
    const prepared = await prepareSubscription(surface, true);
    if ('outcome' in prepared) return prepared.outcome;
    await subscribePush(prepared.subscription.toJSON() as PushSubscriptionJSON, navigator.userAgent);
    return 'subscribed';
  }, [surface]);

  const claimTaskAlerts = useCallback(async (): Promise<ClaimOutcome> => {
    // Written before the first await so a navigation/interruption preserves a
    // durable presentation + boot-retry signal. The server transaction is the
    // only operation that moves ownership away from the former surface.
    markTransferIncomplete(surface);
    const prepared = await prepareSubscription(surface, true);
    if ('outcome' in prepared) return prepared.outcome;
    try {
      await claimTaskAlertsApi(surface, prepared.subscription.toJSON() as PushSubscriptionJSON);
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: { detail?: unknown } } }).response;
      if (response?.status === 409 && response.data?.detail === 'device_registration_required') {
        return 'device_registration_required';
      }
      throw error;
    }
    clearTransferIncomplete(surface);
    return 'claimed';
  }, [surface]);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration(surfaceConfig(surface).workerScope);
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await unsubscribePush(endpoint);
  }, [surface]);

  const unsubscribeDeviceRegistrations = useCallback(async (): Promise<DevicePushEndpointProof> => {
    let known;
    try {
      known = await listKnownPushSubscriptions();
    } catch {
      return { endpoints: [], resolved: false };
    }
    const endpoints = known.map(({ endpoint }) => endpoint);
    await Promise.all(known.map(async ({ endpoint, subscription }) => {
      // Keep browser and backend attempts independent. A browser rejection
      // must not prevent best-effort row cleanup, and the later authenticated
      // device logout remains decisive if either one fails.
      await Promise.allSettled([
        subscription.unsubscribe(),
        unsubscribePush(endpoint),
      ]);
    }));
    return { endpoints, resolved: true };
  }, []);

  return { subscribe, claimTaskAlerts, unsubscribe, unsubscribeDeviceRegistrations };
}

/** P3-A12 boot-time resubscribe/rotation path (PP15). Mounted once in
 * App.tsx, same pattern as `useAppBadge` — self-guards on `user` so it's
 * safe to mount unconditionally alongside the app's other always-on hooks.
 *
 * Handles the case a plain toggle-click can't: a RETURNING user who
 * already granted Notification permission and has the buzz toggle on, but
 * whose stored browser subscription has gone stale (e.g. ops rotated the
 * VAPID keypair, or the push service silently dropped the endpoint) — this
 * calls `subscribe()`, which detects and repairs that mismatch on its own
 * (see PP15 above), without waiting for the user to revisit MySettings.
 * Deliberately does NOT request permission itself — if permission isn't
 * already 'granted', there is nothing to fix here; the coach/settings
 * toggle are the only places that ever prompt. */
export function usePushBootResubscribe(surface: AppSurface = 'valentine'): void {
  const { data: user } = useCurrentUser();
  const { claimTaskAlerts, subscribe } = usePushSubscription(surface);

  useEffect(() => {
    const wantsAnyPush = Boolean(user?.buzz_urgent_tasks || user?.buzz_task_completions);
    const retryTransfer = transferIncompleteFor(surface);
    if (!user || (!wantsAnyPush && !retryTransfer)) return;
    if (!retryTransfer && !isStandaloneDisplayMode()) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const operation = retryTransfer ? claimTaskAlerts : subscribe;
    void operation().catch(() => {
      // Best-effort — a failure here just means the device stays on its
      // existing (possibly stale) subscription until the next boot or a
      // manual toggle; never worth surfacing to the user.
    });
  }, [claimTaskAlerts, subscribe, surface, user]);
}
